import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '../lib/cn';

/**
 * MyHome+ button.
 *
 * The gold pill is the brand cue from the logo work, so it is the primary
 * call to action and nothing else (DESIGN.md "Layout and space"). Gold with
 * white text is 2.1:1 and fails, so gold always carries navy-900 text.
 *
 * Every size is at least 44px tall. No default shadows: elevation comes from
 * borders and tonal surfaces.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap',
    'font-sans font-semibold',
    'transition-colors duration-(--duration-fast) ease-(--ease-enter)',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-focus-light)',
    'disabled:pointer-events-none disabled:opacity-55',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        /** The one primary action on a screen. Gold pill, navy text. */
        primary: [
          'rounded-(--radius-pill) bg-gold-400 text-navy-900',
          'hover:bg-gold-500 active:bg-gold-500',
        ],
        /** Supporting action beside a primary one. */
        secondary: [
          'rounded-(--radius-input) bg-navy-600 text-brand-white',
          'hover:bg-navy-700 active:bg-navy-700',
        ],
        /** Low emphasis action on a light surface. */
        outline: [
          'rounded-(--radius-input) border border-border-strong bg-surface text-navy-900',
          'hover:bg-navy-50 active:bg-navy-100',
        ],
        ghost: ['rounded-(--radius-input) text-navy-900', 'hover:bg-navy-100 active:bg-navy-200'],
        /** Destructive, and it says so in words as well as colour. */
        danger: [
          'rounded-(--radius-input) bg-danger-500 text-brand-white',
          'hover:bg-danger-700 active:bg-danger-700',
        ],
        /** Inline text action. Underlined so it is not colour alone. */
        link: 'rounded-sm text-link underline underline-offset-4 hover:text-navy-600',
        /** On an indigo surface: hero, membership card, Verify Me. */
        onDark: [
          'rounded-(--radius-pill) bg-brand-white text-navy-900',
          'hover:bg-navy-50 active:bg-navy-100',
          'focus-visible:outline-(--color-focus-dark)',
        ],
      },
      size: {
        /** 44px, the minimum touch target. */
        md: 'min-h-(--spacing-touch) px-5 text-body [&_svg]:size-5',
        /** 52px, for the one action that matters most on a page. */
        lg: 'min-h-13 px-6 text-lead [&_svg]:size-6',
        /**
         * Compact, for dense rows and the admin back office. Still 44px of
         * touch area via padding on coarse pointers.
         */
        sm: 'min-h-9 px-3.5 text-meta [&_svg]:size-4 max-[1023px]:min-h-(--spacing-touch)',
        icon: 'size-(--spacing-touch) [&_svg]:size-5',
      },
      /** Full width, for sticky bottom actions and forms on a phone. */
      block: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // A link should not carry button padding or a minimum height.
      { variant: 'link', class: 'min-h-0 px-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      block: false,
    },
  },
);

export interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, block, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot.Root : 'button';

  return (
    <Component
      data-slot="button"
      data-variant={variant ?? 'primary'}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
